import { useCallback, useEffect, useState } from 'react';
import { Toast } from 'antd-mobile';
import i18n from '../../../i18n';
import { referralApi } from '../../../api/referral';
import { ApiEnvelope, PaginatedApiEnvelope } from '../../../api/types';
import { DailyPointSummary, ProfilePointsState } from './types';

const DEFAULT_PAGINATION = {
  total: 0,
  page: 1,
  pageSize: 30,
  totalPages: 0,
};

function readErrorMessage(error: { response?: { data?: { error?: { message?: string } } } }, fallback: string): string {
  return error.response?.data?.error?.message || fallback;
}

export function useProfilePointsModel() {
  const [state, setState] = useState<ProfilePointsState>({
    loading: true,
    totalPoints: 0,
    items: [],
    pagination: DEFAULT_PAGINATION,
  });

  const load = useCallback(async () => {
    setState((previous) => ({ ...previous, loading: true }));
    try {
      const [overviewResponse, dailyResponse] = await Promise.all([
        referralApi.getMyOverview() as Promise<{ data: ApiEnvelope<{ total_points: number }> }>,
        referralApi.getMyDailyRewards({ page: 1, pageSize: DEFAULT_PAGINATION.pageSize }) as Promise<{
          data: PaginatedApiEnvelope<DailyPointSummary>;
        }>,
      ]);

      setState({
        loading: false,
        totalPoints: overviewResponse.data.data.total_points || 0,
        items: dailyResponse.data.data || [],
        pagination: dailyResponse.data.pagination || DEFAULT_PAGINATION,
      });
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as { response?: { data?: { error?: { message?: string } } } }, i18n.t('profile:points.loadFailed')),
      });
      setState((previous) => ({ ...previous, loading: false }));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    state,
    reload: load,
  };
}
